import React, { useCallback, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import AppLeftSideBar from "../../components/SideBar/AppLeftSideBar";
import Button from "../../components/FormComponents/Button";
import {
  getAuditExportRuns,
  getAuditFilters,
  resetAuditExportRuns,
  triggerAuditExportRun
} from "../../services/apis/auditApis";
import { formatDateTime, statusPillClass } from "./utils";
import "../Monitor/monitor.css";
import "./audit.css";

const POLL_MS = 5000;

const AuditExportRuns = ({ embedded = false, orgId }) => {
  const [runs, setRuns] = useState([]);
  const [apps, setApps] = useState([]);
  const [appScope, setAppScope] = useState("");
  const [triggering, setTriggering] = useState(false);
  const [resetting, setResetting] = useState(false);
  const pollRef = useRef(null);

  const fetchRuns = useCallback(async () => {
    try {
      const response = await getAuditExportRuns({ limit: 200 });
      const data = response?.data;
      setRuns(Array.isArray(data) ? data : []);
      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.error("Failed to fetch export runs:", error);
      return [];
    }
  }, []);

  const hasRunning = runs.some((r) => r?.status === "running");

  // poll while any run is in progress
  useEffect(() => {
    if (hasRunning && !pollRef.current) {
      pollRef.current = setInterval(fetchRuns, POLL_MS);
    }
    if (!hasRunning && pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [hasRunning, fetchRuns]);

  useEffect(() => {
    fetchRuns();
    // all provisioned apps (not just ones already exported) so a first-time
    // export can be scoped to a new app
    getAuditFilters({ all: "true", org_id: orgId || undefined })
      .then((response) => setApps(response?.data?.apps || []))
      .catch(() => setApps([]));
  }, [fetchRuns, orgId]);

  const handleTrigger = async () => {
    setTriggering(true);
    try {
      const response = await triggerAuditExportRun(appScope || undefined);
      toast.success(`Export run started (${response?.data?.run_id?.slice(0, 8) || "…"})`);
      await fetchRuns();
    } catch (error) {
      // 400/404 (bad app_id) and 409 (already in progress) are toasted by the
      // axios error handler
      console.error("Failed to trigger export run:", error);
    } finally {
      setTriggering(false);
    }
  };

  const handleReset = async () => {
    setResetting(true);
    try {
      const response = await resetAuditExportRuns();
      const n = response?.data?.reconciled ?? 0;
      toast.success(n > 0 ? `Cleared ${n} stuck run${n === 1 ? "" : "s"}` : "Export state reset");
      await fetchRuns();
    } catch (error) {
      console.error("Failed to reset export state:", error);
    } finally {
      setResetting(false);
    }
  };

  const content = (
    <div className={`d_flex flex_column gap_2 w_100 ${embedded ? "flex_1" : "h_100 p_3"}`} style={{ minHeight: 0 }}>
      <div className="w_100 d_flex justify_between align_center">
        {!embedded && <h2>Audit Exports</h2>}
        <div className="d_flex gap_2 align_center ml_auto">
          <select className="builder_input" value={appScope} onChange={(e) => setAppScope(e.target.value)}>
            <option value="">All apps</option>
            {apps.map((app) => (
              <option key={app.app_id} value={app.app_id}>
                {app.app_name}
              </option>
            ))}
          </select>
          <Button
            variant="primary"
            onClick={handleTrigger}
            disabled={triggering || hasRunning}
            title={hasRunning ? "An export run is already in progress" : "Start an export run"}
          >
            {hasRunning ? "Run in progress…" : "Run export now"}
          </Button>
          {hasRunning && (
            <Button
              variant="secondary"
              onClick={handleReset}
              isLoading={resetting}
              disabled={resetting}
              title="Clear a stuck/orphaned run (marks lingering 'running' rows interrupted and releases the lock)"
            >
              Reset stuck run
            </Button>
          )}
          <Button variant="secondary" onClick={fetchRuns} title="Refresh run history">
            Refresh
          </Button>
        </div>
      </div>

      <div className="monitor_container audit_fill">
        <div className="table-wrapper">
          <table className="log-table">
            <thead>
              <tr>
                <th>Run</th>
                <th>Source</th>
                <th>Status</th>
                <th>Started</th>
                <th>Finished</th>
                <th>Rows</th>
                <th>Error</th>
              </tr>
            </thead>
            <tbody>
              {runs.length === 0 ? (
                <tr>
                  <td colSpan={7} style={{ textAlign: "center", padding: "16px", color: "#6b7280" }}>
                    No export runs yet.
                  </td>
                </tr>
              ) : (
                runs.map((run, index) => {
                  const firstOfRun = index === 0 || runs[index - 1]?.run_id !== run?.run_id;
                  return (
                    <tr key={`${run?.run_id}-${run?.source_name}-${index}`}>
                      <td className="audit_mono" title={run?.run_id}>
                        {firstOfRun ? run?.run_id?.slice(0, 8) : ""}
                      </td>
                      <td className="audit_mono">{run?.source_name}</td>
                      <td>
                        <span className={`status_pill ${statusPillClass(run?.status)}`}>{run?.status}</span>
                      </td>
                      <td>{formatDateTime(run?.started_at)}</td>
                      <td>{formatDateTime(run?.finished_at)}</td>
                      <td>{run?.rows_exported ?? "—"}</td>
                      <td
                        style={{
                          maxWidth: "320px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap"
                        }}
                        title={run?.error || ""}
                      >
                        {run?.error || "—"}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  if (embedded) return content;
  return (
    <div className="application_container">
      <AppLeftSideBar type="org" />
      {content}
    </div>
  );
};

export default AuditExportRuns;
